import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { couchbaseAuthTypes, CouchbaseLinkedService } from 'app/model/linkedservice/couchbase.linkedservice';
import { akvToResource, akvToModel, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from '../../../shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { Constants } from '../../../shared/shared-constants';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from '../../linkedservice/key-vault';
import { AdapterContext } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(CouchbaseLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: CouchbaseLinkedService) => {
                    // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                    // won't cover fully by json for now
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        if (ls.authMech === couchbaseAuthTypes.NoAuthentication) {
                            return '*';
                        }
                        if (ls.authMech === couchbaseAuthTypes.UseCredentials && ls.credString instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                credString: 'secret',
                encryptedCredential: '*'
            }
        }
    }
})
export class CouchbaseLinkedServiceAdapter extends LinkedServiceAdapter<CouchbaseLinkedService> {
    protected modelConstructor(): new () => CouchbaseLinkedService {
        return CouchbaseLinkedService;
    }

    protected model(): IModel {
        return CouchbaseLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: CouchbaseLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.CouchbaseLinkedService;
        properties.type = Metamodel.modelFor(CouchbaseLinkedService).name;

        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Server', linkedService.server);
            builder.add('Port', linkedService.port ? linkedService.port.toString() : '');
            builder.add('AuthMech', linkedService.authMech != null ? linkedService.authMech.toString() : '');
            if (linkedService.authMech === 1 && linkedService.credString && !(linkedService.credString instanceof AzureKeyVault)) {
                builder.add('CredString', linkedService.credString);
            }
            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.server === Constants.maskedCredential && linkedService.port === undefined
                && linkedService.authMech === undefined && linkedService.credString === Constants.maskedCredential ?
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }
        properties.typeProperties = {
            connectionString,
            credString: !linkedService.enableAKV && linkedService.credString instanceof AzureKeyVault ? akvToResource(linkedService.credString, this.context) : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: CouchbaseLinkedService): Promise<CouchbaseLinkedService> {
        const properties = linkedServiceResource.properties as Model.CouchbaseLinkedService;
        linkedService.server = undefined;
        linkedService.port = undefined;
        linkedService.authMech = undefined;
        linkedService.credString = undefined;

        if (isString(properties.typeProperties.connectionString)) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.parse(properties.typeProperties.connectionString as string);

            linkedService.server = builder.getValue('Server', false) || '';
            linkedService.port = parseInt(builder.getValue('Port', false), 10) || undefined;
            linkedService.authMech = parseInt(builder.getValue('AuthMech', false), 10) || 0;
            if (linkedService.authMech === 1) {
                linkedService.credString = Constants.maskedCredential;
            }
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'AuthMech', 'CredString']);
            if (properties.typeProperties.credString) {
                linkedService.credString = secretBaseToPasswordOrAKV(properties.typeProperties.credString, false, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.server = Constants.maskedCredential;
            linkedService.port = undefined;
            linkedService.authMech = undefined;
            linkedService.credString = Constants.maskedCredential;
            if (properties.typeProperties.credString) {
                linkedService.credString = secretBaseToPasswordOrAKV(properties.typeProperties.credString, false, this.context);
            }
        } else if (properties.typeProperties.connectionString && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(properties.typeProperties.connectionString as Model.SecretBase, this.context);
            linkedService.enableAKV = true;
        }
        return linkedService;
    }
}
